// frontend/src/components/players/PDFViewer.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSecureMedia } from '../../services/mediaService';
import * as pdfjsLib from 'pdfjs-dist';

// ✅ استفاده از فایل worker محلی
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

// تنظیمات بهینه برای PDF
const PDF_JS_OPTIONS = {
  disableAutoFetch: true,
  disableStream: false,
  useWorkerFetch: true,
  useSystemFonts: true,
  cMapUrl: 'https://cdn.jsdelivr.net/npm/pdfjs-dist@5.4.296/cmaps/',
  cMapPacked: true,
  standardFontDataUrl: 'https://cdn.jsdelivr.net/npm/pdfjs-dist@5.4.296/standard_fonts/',
  maxImageSize: 512 * 1024,
  enableXfa: false,
  stopAtErrors: false,
  fontExtraProperties: false,
  isEvalSupported: false,
  useStandardFontData: true,
  verbosity: 0,
};

// ✅ تنظیمات کیفیت برای PDFهای اسکن شده
const QUALITY_LEVELS = {
  low: { label: 'اقتصادی', dpi: 100 },
  medium: { label: 'متوسط', dpi: 150 },
  high: { label: 'بالا', dpi: 200 },
  ultra: { label: 'عالی', dpi: 300 },
};

const PDFViewer = ({ mediaId, fileUrl, title }) => {
  const { mediaUrl, isLoading: urlLoading, error: urlError, refetch: refetchMediaUrl } =
    useSecureMedia(mediaId);

  // Stateهای مشترک
  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [loading, setLoading] = useState(true);
  const [renderProgress, setRenderProgress] = useState(0);
  const [error, setError] = useState(null);
  
  // Stateهای Canvas (PDF متنی)
  const [pdfDoc, setPdfDoc] = useState(null);
  const [scale, setScale] = useState(1.2);
  const [containerWidth, setContainerWidth] = useState(0);
  
  // Stateهای تصویری (PDF اسکن شده)
  const [isScannedPDF, setIsScannedPDF] = useState(null); // null = در حال تشخیص
  const [imageUrl, setImageUrl] = useState(null);
  const [quality, setQuality] = useState('medium');
  
  // Refs
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const imgRef = useRef(null);
  const loadingTaskRef = useRef(null);
  const renderTaskRef = useRef(null);
  const isMounted = useRef(true);

  const directPdf = fileUrl || mediaUrl;
  const proxyUrl = directPdf?.startsWith('https://s3.afra.kubit.cloud/')
    ? `https://farzaneh.app/api/proxy/media/?url=${encodeURIComponent(directPdf)}`
    : directPdf;

  // ✅ مرحله ۱: تشخیص نوع PDF (متنی یا اسکن شده)
  useEffect(() => {
    if (!proxyUrl) return;

    const detectPDFType = async () => {
      try {
        setLoading(true);
        setRenderProgress(10);
        
        const loadingTask = pdfjsLib.getDocument({
          url: proxyUrl,
          ...PDF_JS_OPTIONS,
        });
        
        loadingTaskRef.current = loadingTask;
        
        loadingTask.onProgress = (progress) => {
          if (isMounted.current) {
            const percent = Math.round((progress.loaded / progress.total) * 50);
            setRenderProgress(10 + percent);
          }
        };

        const pdf = await loadingTask.promise;
        
        if (!isMounted.current) return;
        
        // بررسی صفحه اول
        const page = await pdf.getPage(1);
        const textContent = await page.getTextContent();
        
        // اگر تعداد کاراکترها کمتر از ۵۰ باشه، احتمالاً اسکن شده است
        const textLength = textContent.items.reduce((sum, item) => sum + item.str.length, 0);
        const isScanned = textLength < 50;
        
        console.log(`📄 PDF Type: ${isScanned ? 'Scanned/Image' : 'Text'} (text length: ${textLength})`);
        
        setIsScannedPDF(isScanned);
        setNumPages(pdf.numPages);
        setRenderProgress(100);
        
        if (!isScanned) {
          // PDF متنی - نگه داشتن pdfDoc برای رندر با Canvas
          setPdfDoc(pdf);
          setLoading(false);
        } else {
          // PDF اسکن شده - پاک کردن pdfDoc
          setPdfDoc(null);
          pdf.destroy();
          setLoading(false);
        }
        
      } catch (err) {
        console.error('Error detecting PDF type:', err);
        if (isMounted.current) {
          setIsScannedPDF(false);
          setError('خطا در تشخیص نوع فایل');
          setLoading(false);
        }
      }
    };
    
    detectPDFType();

    return () => {
      isMounted.current = false;
      if (loadingTaskRef.current) {
        loadingTaskRef.current.destroy();
      }
    };
  }, [proxyUrl]);

  // ✅ مرحله ۲-A: رندر Canvas برای PDFهای متنی
  const renderPage = useCallback(async () => {
    if (!pdfDoc || !canvasRef.current || isScannedPDF) return;

    try {
      if (renderTaskRef.current) {
        await renderTaskRef.current.cancel();
        renderTaskRef.current = null;
      }

      const page = await pdfDoc.getPage(pageNumber);
      const viewport = page.getViewport({ scale: 1 });
      
      const containerWidth_ = containerWidth || 800;
      const containerHeight = window.innerHeight - 160;
      
      const scaleX = containerWidth_ / viewport.width;
      const scaleY = containerHeight / viewport.height;
      const finalScale = Math.min(Math.max(Math.min(scaleX, scaleY), 0.5), 2.0);
      
      const devicePixelRatio = Math.min(window.devicePixelRatio || 1, 1.5);
      const renderScale = finalScale * devicePixelRatio;

      const scaledViewport = page.getViewport({ scale: renderScale });

      const canvas = canvasRef.current;
      const context = canvas.getContext('2d', { 
        alpha: false,
        willReadFrequently: false,
      });

      canvas.width = scaledViewport.width;
      canvas.height = scaledViewport.height;
      
      const displayWidth = scaledViewport.width / devicePixelRatio;
      const displayHeight = scaledViewport.height / devicePixelRatio;
      canvas.style.width = `${displayWidth}px`;
      canvas.style.height = `${displayHeight}px`;

      const renderContext = {
        canvasContext: context,
        viewport: scaledViewport,
        background: '#fff',
        enableWebGL: false,
        renderInteractiveForms: false,
        enableXfa: false,
        intent: 'display',
        annotationMode: 0,
        useSystemFonts: true,
        fontExtraProperties: true,
      };

      const renderTask = page.render(renderContext);
      renderTaskRef.current = renderTask;
      
      await renderTask.promise;
      renderTaskRef.current = null;
      
      setScale(finalScale);
    } catch (error) {
      if (error.name !== 'RenderingCancelledException') {
        console.error('Render error:', error);
      }
    }
  }, [pdfDoc, pageNumber, containerWidth, isScannedPDF]);

  useEffect(() => {
    if (pdfDoc && !loading && !isScannedPDF) {
      renderPage();
    }
  }, [renderPage, pdfDoc, loading, isScannedPDF]);

  // ✅ مرحله ۲-B: بارگذاری تصویر برای PDFهای اسکن شده
  const getImageUrl = useCallback((page, q = quality) => {
    if (!mediaId) return null;
    const baseUrl = `/api/v1/products/pdf-to-image/${mediaId}/`;
    return `${baseUrl}?page=${page}&quality=${q}`;
  }, [mediaId, quality]);

  useEffect(() => {
    if (!isScannedPDF || numPages === 0) return;
    
    const url = getImageUrl(pageNumber, quality);
    if (url) {
      setImageUrl(url);
      setLoading(true);
    }
  }, [pageNumber, quality, numPages, isScannedPDF, getImageUrl]);

  // ✅ پیش‌بارگذاری صفحات بعدی (فقط برای اسکن شده)
  useEffect(() => {
    if (!isScannedPDF || numPages === 0 || !mediaId) return;

    for (let i = pageNumber + 1; i <= Math.min(pageNumber + 2, numPages); i++) {
      const img = new Image();
      img.src = getImageUrl(i, quality);
    }
  }, [pageNumber, numPages, quality, mediaId, isScannedPDF, getImageUrl]);

  // اندازه‌گیری عرض کانتینر
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        const width = containerRef.current.clientWidth;
        setContainerWidth(width - 40 || 800);
      } else {
        setContainerWidth(800);
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  // کنترل‌های مشترک
  const changePage = useCallback((offset) => {
    setPageNumber(prev => {
      const newPage = prev + offset;
      return Math.max(1, Math.min(newPage, numPages));
    });
  }, [numPages]);

  const goToPage = (page) => {
    const newPage = Math.max(1, Math.min(page, numPages));
    setPageNumber(newPage);
  };

  const zoomIn = () => setScale(prev => Math.min(prev + 0.2, 2.5));
  const zoomOut = () => setScale(prev => Math.max(prev - 0.2, 0.5));
  const zoomFit = () => {
    if (containerRef.current && pdfDoc) {
      renderPage();
    }
  };

  const cycleQuality = () => {
    const qualities = Object.keys(QUALITY_LEVELS);
    const currentIndex = qualities.indexOf(quality);
    const nextIndex = (currentIndex + 1) % qualities.length;
    setQuality(qualities[nextIndex]);
  };

  // جلوگیری از دانلود
  const handleContextMenu = (e) => { e.preventDefault(); };
  const handleDragStart = (e) => { e.preventDefault(); };

  const handleKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && (e.key === 's' || e.key === 'p')) {
      e.preventDefault();
    }
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      changePage(1);
    }
    if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      changePage(-1);
    }
    // کلیدهای +/- برای تغییر کیفیت (فقط در حالت اسکن شده)
    if (isScannedPDF) {
      if (e.key === '+' || e.key === '=') {
        e.preventDefault();
        const qualities = Object.keys(QUALITY_LEVELS);
        const currentIndex = qualities.indexOf(quality);
        if (currentIndex < qualities.length - 1) {
          setQuality(qualities[currentIndex + 1]);
        }
      }
      if (e.key === '-') {
        e.preventDefault();
        const qualities = Object.keys(QUALITY_LEVELS);
        const currentIndex = qualities.indexOf(quality);
        if (currentIndex > 0) {
          setQuality(qualities[currentIndex - 1]);
        }
      }
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [changePage, quality, isScannedPDF]);

  // Cleanup
  useEffect(() => {
    return () => {
      isMounted.current = false;
      if (loadingTaskRef.current) {
        loadingTaskRef.current.destroy();
      }
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
      }
      if (pdfDoc && isScannedPDF) {
        pdfDoc.destroy();
      }
    };
  }, []);

  // ==================== رندر ====================

  // خطا
  if (urlError || error) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-8 text-center">
        <p className="font-bold text-red-400 mb-4">{error || urlError}</p>
        <button onClick={() => { setError(null); refetchMediaUrl?.(); }} className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg">
          تلاش مجدد
        </button>
      </div>
    );
  }

  // در حال تشخیص نوع PDF
  if (isScannedPDF === null || (loading && isScannedPDF === null)) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-8 text-center">
        <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mb-4" />
        <p className="text-gray-600 mb-2">در حال تشخیص نوع فایل...</p>
        <div className="w-48 h-2 bg-gray-200 rounded-full overflow-hidden">
          <div className="h-full bg-teal-500 transition-all duration-300" style={{ width: `${renderProgress}%` }} />
        </div>
        <p className="text-xs text-gray-400 mt-2">{renderProgress}%</p>
      </div>
    );
  }

  // ==================== PDF متنی - Canvas ====================
  if (!isScannedPDF) {
    if (urlLoading || loading || !proxyUrl) {
      return (
        <div className="min-h-screen bg-white flex flex-col items-center justify-center p-8 text-center">
          <div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin mb-4" />
          <p className="text-gray-600 mb-2">در حال بارگذاری PDF...</p>
          <div className="w-48 h-2 bg-gray-200 rounded-full overflow-hidden">
            <div className="h-full bg-green-500 transition-all duration-300" style={{ width: `${renderProgress}%` }} />
          </div>
          <p className="text-xs text-gray-400 mt-2">{renderProgress}%</p>
        </div>
      );
    }

    return (
      <div className="h-screen flex flex-col bg-oklch(25.7% 0.09 281.288) select-none" onContextMenu={handleContextMenu} onDragStart={handleDragStart} dir="ltr">
        {/* هدر PDF متنی */}
        <div className="flex flex-col bg-white border-b-2 border-teal-700 shadow-sm">
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-2">
              <h2 className="text-base font-semibold text-teal-600 truncate max-w-[150px] sm:max-w-xs">
                {title || 'کتاب'}
              </h2>
              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                📄 متنی
              </span>
            </div>
            <div className="flex items-center gap-3 text-sm text-green-600">
              <span>صفحه {pageNumber}</span>
              <span className="opacity-50">از {numPages}</span>
            </div>
          </div>

          {/* کنترل‌ها */}
          <div className="flex items-center justify-between px-4 py-2 border-t border-teal-200 flex-wrap gap-2">
            <div className="flex items-center gap-1">
              <button onClick={zoomOut} className="p-1.5 rounded hover:bg-green-50 text-teal-600 transition-colors border border-green-200 hover:border-green-400" title="کم کردن بزرگنمایی">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10a1 1 0 10-2 0v1h-1a1 1 0 100 2h1v1a1 1 0 102 0v-1h1a1 1 0 100-2h-1v-1z" />
                </svg>
              </button>
              <span className="text-xs min-w-[45px] text-center text-teal-700">{Math.round(scale * 100)}%</span>
              <button onClick={zoomIn} className="p-1.5 rounded hover:bg-green-50 text-teal-600 transition-colors border border-green-200 hover:border-green-400" title="افزایش بزرگنمایی">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 10v6m-3-3h6" />
                </svg>
              </button>
              <button onClick={zoomFit} className="px-2 py-1 text-xs rounded hover:bg-green-50 text-teal-600 transition-colors border border-green-200 hover:border-teal-400">Fit</button>
            </div>

            <div className="flex items-center gap-1">
              <button onClick={() => changePage(-1)} disabled={pageNumber <= 1} className="p-1.5 rounded hover:bg-green-50 text-teal-600 transition-colors border border-teal-200 hover:border-teal-400 disabled:opacity-40 disabled:cursor-not-allowed">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <input type="number" min={1} max={numPages} value={pageNumber} onChange={(e) => { const val = parseInt(e.target.value); if (val >= 1 && val <= numPages) goToPage(val); }} className="w-12 px-1 py-1 bg-white text-green-700 text-center rounded border border-green-300 focus:outline-none focus:border-green-500 text-sm" />
              <span className="text-xs text-green-600">/ {numPages}</span>
              <button onClick={() => changePage(1)} disabled={pageNumber >= numPages} className="p-1.5 rounded hover:bg-green-50 text-green-600 transition-colors border border-green-200 hover:border-green-400 disabled:opacity-40 disabled:cursor-not-allowed">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            <button onClick={() => { const elem = document.querySelector('.pdf-viewer-container'); if (elem?.requestFullscreen) elem.requestFullscreen(); }} className="p-1.5 rounded hover:bg-green-50 text-green-600 transition-colors border border-green-200 hover:border-green-400" title="تمام صفحه">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5" />
              </svg>
            </button>
          </div>
        </div>

        {/* Canvas PDF متنی */}
        <div ref={containerRef} className="flex-1 flex items-center justify-center bg-gray-50 p-2 sm:p-4 overflow-auto">
          <div className="relative shadow-xl bg-white rounded-lg p-2">
            <canvas ref={canvasRef} className="bg-white" />
            <div className="absolute inset-0 pointer-events-none opacity-5 flex items-center justify-center">
              <span className="text-4xl font-bold text-gray-500 rotate-[-30deg]">FARZANEH</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ==================== PDF اسکن شده - تصویری ====================
  if (loading || !imageUrl) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-8 text-center">
        <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mb-4" />
        <p className="text-gray-600 mb-2">در حال بارگذاری تصویر با کیفیت بالا...</p>
        <p className="text-xs text-teal-600 mt-2">
          (کیفیت: {QUALITY_LEVELS[quality]?.label} - {QUALITY_LEVELS[quality]?.dpi} DPI)
        </p>
        <p className="text-xs text-amber-600 mt-1">📸 PDF اسکن شده</p>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50 select-none" onContextMenu={handleContextMenu} onDragStart={handleDragStart} dir="ltr">
      {/* هدر PDF اسکن شده */}
      <div className="bg-teal-800 text-white px-4 py-3 shadow-lg">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-1">
            <button onClick={cycleQuality} className={`px-2 py-1 text-xs rounded transition-colors ${quality === 'ultra' ? 'bg-amber-600' : quality === 'high' ? 'bg-teal-600' : 'bg-gray-600'} hover:bg-teal-700`} title="تغییر کیفیت (کلیدهای + و -)">
              {QUALITY_LEVELS[quality]?.label}
            </button>
            <span className="text-xs bg-teal-700 px-2 py-0.5 rounded-full">{QUALITY_LEVELS[quality]?.dpi} DPI</span>
            <span className="text-xs bg-amber-600 px-2 py-0.5 rounded-full">📸 اسکن شده</span>
          </div>

          <div className="flex items-center gap-1">
            <button onClick={() => changePage(-1)} disabled={pageNumber <= 1} className="p-1.5 rounded hover:bg-teal-700 transition-colors disabled:opacity-40">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <input type="number" min={1} max={numPages} value={pageNumber} onChange={(e) => { const val = parseInt(e.target.value); if (val >= 1 && val <= numPages) goToPage(val); }} className="w-12 px-1 py-1 bg-white/20 text-white text-center rounded border border-white/30 focus:outline-none focus:border-white text-sm" style={{ fontFamily: 'Vazirmatn, sans-serif' }} />
            <span className="text-xs opacity-75" style={{ fontFamily: 'Vazirmatn, sans-serif' }}>/ {numPages}</span>
            <button onClick={() => changePage(1)} disabled={pageNumber >= numPages} className="p-1.5 rounded hover:bg-teal-700 transition-colors disabled:opacity-40">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          <button onClick={() => { const elem = document.querySelector('.pdf-viewer-container'); if (elem?.requestFullscreen) elem.requestFullscreen(); }} className="p-1.5 rounded hover:bg-teal-700">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5" />
            </svg>
          </button>
        </div>
      </div>

      {/* تصویر PDF اسکن شده */}
      <div ref={containerRef} className="flex-1 flex items-center justify-center bg-gray-50 p-2 sm:p-4 overflow-auto">
        <div className="relative shadow-xl bg-white rounded-lg p-2">
          <img ref={imgRef} src={imageUrl} alt={`صفحه ${pageNumber}`} className="max-w-full h-auto" onLoad={() => setLoading(false)} onError={() => { setLoading(false); setError('خطا در بارگذاری تصویر'); }} />
          <div className="absolute inset-0 pointer-events-none opacity-5 flex items-center justify-center">
            <span className="text-4xl font-bold text-gray-500 rotate-[-30deg]">FARZANEH</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PDFViewer;